<template>
  <div v-if="showBlock" class="link-redirects">
    <h3 v-if="showAccessHeader" class="link-redirects__title">
      {{ accessTitle }}
    </h3>
    <p v-if="showAccessLead" class="link-redirects__lead">
      {{ programAccessLead }}
    </p>
    <div
      v-if="subscriberKeyFetchState === 'loading'"
      class="link-redirects__loading"
      role="status"
      aria-live="polite"
    >
      <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
      <span>{{ $t("labels.programAccessLoading") }}</span>
    </div>
    <template v-else-if="links.length > 0">
      <a
        v-for="(link, idx) in links"
        :key="idx"
        :href="link.url"
        target="_blank"
        rel="noopener noreferrer"
        class="link-redirect-btn"
      >
        {{ $t("labels.programAccessLink")
        }}<template v-if="link.label"> « {{ link.label }} »</template>
      </a>
    </template>
  </div>
</template>

<script>
import { useOrderHistoryStore } from "@/store/orderHistory";
import { resolveProgramLinks } from "@/services/programLinks";
import {
  fetchSubscriberKey,
  getBillingEmailFromHistory,
  needsSubscriberKeyFetch,
} from "@/services/subscriberKey";

export default {
  name: "CheckoutLinkRedirects",
  data() {
    return {
      links: [],
      subscriberKeyFetchState: "idle",
      fetchRequestId: 0,
    };
  },
  computed: {
    orderHistoryLastUpdate() {
      return useOrderHistoryStore().lastUpdate;
    },
    showBlock() {
      return (
        this.subscriberKeyFetchState === "loading" || this.links.length > 0
      );
    },
    showAccessHeader() {
      return (
        this.subscriberKeyFetchState === "loading" ||
        (this.links.length > 0 && this.linkRedirectsNeedSubscriberKey)
      );
    },
    showAccessLead() {
      return this.showAccessHeader && this.programAccessLead;
    },
    linkRedirectsNeedSubscriberKey() {
      const redirects = window.productDetails?.linkRedirects || [];
      return redirects.some((redirect) =>
        /%%subscriberkey%%/i.test(redirect.url || ""),
      );
    },
    accessTitle() {
      const redirects = window.productDetails?.linkRedirects || [];
      return redirects.length > 1
        ? this.$t("labels.yourAccess")
        : this.$t("labels.yourAccessSingular");
    },
    programAccessLead() {
      const redirects = window.productDetails?.linkRedirects || [];
      return redirects.length > 1
        ? this.$t("labels.programAccessLead")
        : this.$t("labels.programAccessLeadSingular");
    },
  },
  watch: {
    orderHistoryLastUpdate() {
      this.loadLinks();
    },
  },
  mounted() {
    this.loadLinks();
  },
  methods: {
    async loadLinks() {
      const store = useOrderHistoryStore();
      const history = store.activeOrderHistory || [];
      const requestId = ++this.fetchRequestId;

      if (needsSubscriberKeyFetch(history)) {
        this.subscriberKeyFetchState = "loading";
        this.links = [];

        try {
          const email = getBillingEmailFromHistory(history);
          const subscriberKey = await fetchSubscriberKey(email);
          if (requestId !== this.fetchRequestId) {
            return;
          }
          store.setBillingSubscriberKey(subscriberKey);
          this.subscriberKeyFetchState = "ready";
        } catch (error) {
          if (requestId !== this.fetchRequestId) {
            return;
          }
          console.error(error);
          this.subscriberKeyFetchState = "failed";
          this.links = [];
          return;
        }
      } else {
        this.subscriberKeyFetchState = "idle";
      }

      if (requestId !== this.fetchRequestId) {
        return;
      }

      this.links = resolveProgramLinks();
    },
  },
};
</script>

<style scoped>
.link-redirects {
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}
.link-redirects__title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: #212529;
}
.link-redirects__lead {
  margin: 0 0 4px;
  color: #495057;
}
.link-redirects__loading {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #495057;
  font-size: 0.95rem;
}
.link-redirect-btn {
  display: inline-block;
  padding: 8px 18px;
  background-color: #4a90d9;
  color: #fff;
  border-radius: 5px;
  text-decoration: none;
  font-weight: 600;
  font-size: 13px;
  transition: background-color 0.2s;
}
.link-redirect-btn:hover {
  background-color: #357abd;
  color: #fff;
}
</style>
